import { Alert, Button, InputNumber, Select, Space, Typography, notification } from "antd";
import { useContext, useEffect, useState } from "react";

import { Context } from "../../context";
import { Fetch, Put } from "../../fetch";

const { Title } = Typography;

/**
 * Task Config page — the global coin-surface switches in `tasks_config`,
 * one card per config doc, each editable live (no deploy/restart, setu
 * caches ~60s):
 *   watch_campaign_config.maxEpisodesPerDay — daily video watch cap
 *   app_gate_config.coinsMinAppVersion     — coin-surface force-upgrade gate
 *   integrity_config.watchIntegrityMode    — watch-earn device attestation
 * (login_integrity_config has its own "Login Integrity" sidebar page.)
 */

const WATCH_INTEGRITY_MODES = [
  { value: "off", label: "off — no attestation checks" },
  { value: "dry_run", label: "dry_run — attest + log, always pay" },
  { value: "enforce", label: "enforce — withhold only devices whose last verdict was BAD" },
  { value: "strict", label: "strict — withhold unless currently attested-good" },
];

const useConfigDoc = (apiUrl, token, kind, onLoad) => {
  useEffect(() => {
    Fetch(`${apiUrl}/v4/tasks-config/config/${kind}`, token, (result) => {
      if (result && result.success !== false) {
        onLoad(result.data || {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token]);
};

const saveConfigDoc = (apiUrl, token, kind, data, setSaving) => {
  setSaving(true);
  Put(
    `${apiUrl}/v4/tasks-config/config/${kind}`,
    token,
    { data },
    (result) => {
      setSaving(false);
      if (result.success) {
        notification.success({
          message: "Success",
          description: `${kind} updated — live within ~1 minute`,
        });
      } else {
        notification.error({
          message: `Error updating ${kind}`,
          description: (result.detail && result.detail.message) || "Check console for more details",
        });
      }
    },
    () => {
      setSaving(false);
      notification.error({
        message: `Error updating ${kind}`,
        description: "Check console for more details",
      });
    }
  );
};

const AppConfig = () => {
  const { token, apiUrl } = useContext(Context);

  const [maxPerDay, setMaxPerDay] = useState(60);
  const [savingWatch, setSavingWatch] = useState(false);

  const [minAppVersion, setMinAppVersion] = useState(0);
  const [savingGate, setSavingGate] = useState(false);
  const [vcMinAppVersion, setVcMinAppVersion] = useState(0);
  const [savingVcGate, setSavingVcGate] = useState(false);

  const [watchIntegrityMode, setWatchIntegrityMode] = useState("off");
  const [savingIntegrity, setSavingIntegrity] = useState(false);

  const [noAdEpisodeThreshold, setNoAdEpisodeThreshold] = useState(0);
  const [noAdMaxAdsShown, setNoAdMaxAdsShown] = useState(0);
  const [noAdCoinAmount, setNoAdCoinAmount] = useState(1);
  const [savingNoAdCoin, setSavingNoAdCoin] = useState(false);

  const [quizZeroAdLevelThreshold, setQuizZeroAdLevelThreshold] = useState(0);
  const [quizZeroAdMaxAdsShown, setQuizZeroAdMaxAdsShown] = useState(0);
  const [quizZeroAdCoinAmount, setQuizZeroAdCoinAmount] = useState(1);
  const [savingQuizZeroAdCoin, setSavingQuizZeroAdCoin] = useState(false);

  const [gyanniZeroAdLevelThreshold, setGyanniZeroAdLevelThreshold] = useState(1);
  const [gyanniZeroAdMaxAdsShown, setGyanniZeroAdMaxAdsShown] = useState(0);
  const [gyanniZeroAdCoinAmount, setGyanniZeroAdCoinAmount] = useState(1);
  const [savingGyanniZeroAdCoin, setSavingGyanniZeroAdCoin] = useState(false);

  useConfigDoc(apiUrl, token, "watch_campaign_config", (d) =>
    setMaxPerDay(d.maxEpisodesPerDay ?? 60)
  );
  useConfigDoc(apiUrl, token, "app_gate_config", (d) => {
    setMinAppVersion(d.coinsMinAppVersion ?? 0);
    setVcMinAppVersion(d.vcommissionMinAppVersion ?? 0);
  });
  useConfigDoc(apiUrl, token, "integrity_config", (d) =>
    setWatchIntegrityMode(d.watchIntegrityMode ?? "off")
  );
  useEffect(() => {
    Fetch(`${apiUrl}/v4/app_config/admin/player-no-ad-coin`, token, (result) => {
      if (result && result.success !== false && result.data) {
        setNoAdEpisodeThreshold(result.data.noAdCoinEpisodeThreshold ?? 0);
        setNoAdMaxAdsShown(result.data.noAdCoinMaxAdsShown ?? 0);
        setNoAdCoinAmount(result.data.noAdCoinAmount ?? 1);
      }
    });
    Fetch(`${apiUrl}/v4/app_config/admin/quiz-zero-ad-coin`, token, (result) => {
      if (result && result.success !== false && result.data) {
        setQuizZeroAdLevelThreshold(result.data.zeroAdCoinLevelThreshold ?? 0);
        setQuizZeroAdMaxAdsShown(result.data.zeroAdCoinMaxAdsShown ?? 0);
        setQuizZeroAdCoinAmount(result.data.zeroAdCoinAmount ?? 1);
      }
    });
    Fetch(`${apiUrl}/v4/app_config/admin/gyanni-quiz-zero-ad-coin`, token, (result) => {
      if (result && result.success !== false && result.data) {
        setGyanniZeroAdLevelThreshold(result.data.zeroAdCoinLevelThreshold ?? 1);
        setGyanniZeroAdMaxAdsShown(result.data.zeroAdCoinMaxAdsShown ?? 0);
        setGyanniZeroAdCoinAmount(result.data.zeroAdCoinAmount ?? 1);
      }
    });
  }, [apiUrl, token]);

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <Title level={4}>Task Config</Title>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Daily video watch cap"
        description="Max DISTINCT videos one user can earn from per IST day — the single earning throttle across every flow (stepper, ladder, campaign, episode rewards); resets at midnight, 0 disables, default 60. When reached, AG-registered FREE users are also blocked from watching more episodes until tomorrow (app shows the come-back-tomorrow message); MiniPix-only and paid users are never blocked. Takes effect immediately, no deploy or restart."
      />
      <Space size="large" wrap>
        <Space direction="vertical">
          <span>Max videos / day (all flows)</span>
          <InputNumber
            min={0}
            precision={0}
            value={maxPerDay}
            onChange={setMaxPerDay}
          />
        </Space>
      </Space>
      <div style={{ marginTop: 20, marginBottom: 32 }}>
        <Button
          type="primary"
          loading={savingWatch}
          onClick={() =>
            saveConfigDoc(
              apiUrl,
              token,
              "watch_campaign_config",
              { maxEpisodesPerDay: Number(maxPerDay) || 0 },
              setSavingWatch
            )
          }
        >
          Save
        </Button>
      </div>

      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message="App gate — coin-surface force-upgrade (coinsMinAppVersion)"
        description="Minimum Android versionCode allowed to SEE and EARN coins (quiz, watch & earn, tasks). Builds below it — or builds that never send X-App-Version — get enabled:false everywhere and hide the whole coin surface, with no error shown. 0 = gate off. CAUTION: set this only to a versionCode that is actually live on the Play Store at 100% rollout, otherwise fully-updated users are locked out too (this hid the quiz for all v2.0.2/328 users when the gate was set to 330 before v2.0.4 shipped)."
      />
      <Space size="large" wrap>
        <Space direction="vertical">
          <span>Min versionCode for coins (0 = off)</span>
          <InputNumber
            min={0}
            precision={0}
            value={minAppVersion}
            onChange={setMinAppVersion}
          />
        </Space>
      </Space>
      <div style={{ marginTop: 20, marginBottom: 32 }}>
        <Button
          type="primary"
          danger
          loading={savingGate}
          onClick={() =>
            saveConfigDoc(
              apiUrl,
              token,
              "app_gate_config",
              { coinsMinAppVersion: Number(minAppVersion) || 0 },
              setSavingGate
            )
          }
        >
          Save
        </Button>
      </div>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="vCommission offers — min app version (vcommissionMinAppVersion)"
        description="First Android versionCode that opens vCommission offers through setu's attributed tracking link (p1/p2/p3). Older builds would open them like PubScale offers and the conversion could never be credited, so vCommission offers are HIDDEN from builds below this. 0 = hidden from everyone. First capable build: 334."
      />
      <Space size="large" wrap>
        <Space direction="vertical">
          <span>Min versionCode for vCommission offers (0 = hidden)</span>
          <InputNumber
            min={0}
            precision={0}
            value={vcMinAppVersion}
            onChange={setVcMinAppVersion}
          />
        </Space>
      </Space>
      <div style={{ marginTop: 20, marginBottom: 32 }}>
        <Button
          type="primary"
          loading={savingVcGate}
          onClick={() =>
            saveConfigDoc(
              apiUrl,
              token,
              "app_gate_config",
              { vcommissionMinAppVersion: Number(vcMinAppVersion) || 0 },
              setSavingVcGate
            )
          }
        >
          Save
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Watch integrity mode (watchIntegrityMode)"
        description="Device attestation for watch-earn coin credits. off = no checks; dry_run = attest and log but always pay; enforce = withhold only devices whose last heartbeat verdict was BAD; strict = withhold unless the device is currently attested-good (bad OR missing/expired heartbeat). Strict requires an app build that ships the attestation heartbeat — with no such build live, strict withholds watch coins from everyone. A Redis-unreachable read always fails open."
      />
      <Space size="large" wrap>
        <Space direction="vertical">
          <span>Mode</span>
          <Select
            style={{ minWidth: 420 }}
            value={watchIntegrityMode}
            onChange={setWatchIntegrityMode}
            options={WATCH_INTEGRITY_MODES}
          />
        </Space>
      </Space>
      <div style={{ marginTop: 20, marginBottom: 32 }}>
        <Button
          type="primary"
          loading={savingIntegrity}
          onClick={() =>
            saveConfigDoc(
              apiUrl,
              token,
              "integrity_config",
              { watchIntegrityMode },
              setSavingIntegrity
            )
          }
        >
          Save
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Series player — coin when few scheduled ads are shown"
        description="Counted only for the current app open, in memory, across every series. Each episode the user opens or jumps to counts right away, including during the ad loading screen. Watch progress is not checked. Only player ad placements that are turned ON are counted when they actually appear (interstitial, native, banner, or series VAST). Placements that are off — banner, native, rewarded, or anything else disabled — are ignored. They get the coin when the episode total is reached and the ads that appeared are still within Max ads shown. 0 means give the coin only when no enabled ad appeared. 4 means give the coin when 0, 1, 2, 3, or 4 enabled ads appeared. One more than that number does not get the coin. Once for that open. Closing the app clears the count. The next open starts again and can earn again. Paid subscribers are skipped. Episode threshold 0, or coins 0, turns the payout off. Live on the next app config poll — no deploy."
      />
      <Space size="large" wrap>
        <Space direction="vertical">
          <span>Episodes opened (0 = off)</span>
          <InputNumber
            min={0}
            max={500}
            precision={0}
            value={noAdEpisodeThreshold}
            onChange={(v) => setNoAdEpisodeThreshold(v ?? 0)}
          />
        </Space>
        <Space direction="vertical">
          <span>Max ads shown and still give the coin (0 = only when none shown)</span>
          <InputNumber
            min={0}
            max={500}
            precision={0}
            value={noAdMaxAdsShown}
            onChange={(v) => setNoAdMaxAdsShown(v ?? 0)}
          />
        </Space>
        <Space direction="vertical">
          <span>Coins to give</span>
          <InputNumber
            min={0}
            max={100}
            precision={0}
            value={noAdCoinAmount}
            onChange={(v) => setNoAdCoinAmount(v ?? 0)}
          />
        </Space>
      </Space>
      <div style={{ marginTop: 20 }}>
        <Button
          type="primary"
          loading={savingNoAdCoin}
          onClick={() => {
            setSavingNoAdCoin(true);
            Put(
              `${apiUrl}/v4/app_config/admin/player-no-ad-coin`,
              token,
              {
                noAdCoinEpisodeThreshold: Number(noAdEpisodeThreshold) || 0,
                noAdCoinMaxAdsShown: Number(noAdMaxAdsShown) || 0,
                noAdCoinAmount: Number(noAdCoinAmount) || 0,
              },
              (result) => {
                setSavingNoAdCoin(false);
                if (result.success) {
                  notification.success({
                    message: "Success",
                    description: "Player no-ad coin updated — live on the next app config poll",
                  });
                } else {
                  notification.error({
                    message: "Error updating player no-ad coin",
                    description: (result.detail && result.detail.message) || "Check console for more details",
                  });
                }
              },
              () => {
                setSavingNoAdCoin(false);
                notification.error({
                  message: "Error updating player no-ad coin",
                  description: "Check console for more details",
                });
              }
            );
          }}
        >
          Save
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 32, marginBottom: 16 }}
        message="Quiz — coin when a passed level showed no ads (MiniPix only)"
        description="Counted per quiz level, not across the user's whole quiz history. Ads requested during that level are tracked separately from ads that actually appeared (impression / display). Loads, no-fill, and failures do not count as shown. If the user passes the level and no ad was shown, they get this extra coin. If one or more ads were shown, they only get the normal correct-answer coins for passing — this extra coin is 0. Leaving the level unfinished pays 0. 1 = every passed zero-ad level pays. 3 = three such levels pay once. Max ads shown 0 means pay only when none appeared. Level threshold 0, or coins 0, turns the payout off. Paid subscribers are skipped. Same passed level is paid at most once. Live on the next app config poll — no deploy."
      />
      <Space size="large" wrap>
        <Space direction="vertical">
          <span>Passed levels with zero ads (0 = off)</span>
          <InputNumber
            min={0}
            max={500}
            precision={0}
            value={quizZeroAdLevelThreshold}
            onChange={(v) => setQuizZeroAdLevelThreshold(v ?? 0)}
          />
        </Space>
        <Space direction="vertical">
          <span>Max ads shown and still give the coin (0 = only when none shown)</span>
          <InputNumber
            min={0}
            max={500}
            precision={0}
            value={quizZeroAdMaxAdsShown}
            onChange={(v) => setQuizZeroAdMaxAdsShown(v ?? 0)}
          />
        </Space>
        <Space direction="vertical">
          <span>Coins to give</span>
          <InputNumber
            min={0}
            max={100}
            precision={0}
            value={quizZeroAdCoinAmount}
            onChange={(v) => setQuizZeroAdCoinAmount(v ?? 0)}
          />
        </Space>
      </Space>
      <div style={{ marginTop: 20 }}>
        <Button
          type="primary"
          loading={savingQuizZeroAdCoin}
          onClick={() => {
            setSavingQuizZeroAdCoin(true);
            Put(
              `${apiUrl}/v4/app_config/admin/quiz-zero-ad-coin`,
              token,
              {
                zeroAdCoinLevelThreshold: Number(quizZeroAdLevelThreshold) || 0,
                zeroAdCoinMaxAdsShown: Number(quizZeroAdMaxAdsShown) || 0,
                zeroAdCoinAmount: Number(quizZeroAdCoinAmount) || 0,
              },
              (result) => {
                setSavingQuizZeroAdCoin(false);
                if (result.success) {
                  notification.success({
                    message: "Success",
                    description: "Quiz zero-ad coin updated — live on the next app config poll",
                  });
                } else {
                  notification.error({
                    message: "Error updating quiz zero-ad coin",
                    description: (result.detail && result.detail.message) || "Check console for more details",
                  });
                }
              },
              () => {
                setSavingQuizZeroAdCoin(false);
                notification.error({
                  message: "Error updating quiz zero-ad coin",
                  description: "Check console for more details",
                });
              }
            );
          }}
        >
          Save
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 32, marginBottom: 16 }}
        message="Gyanni — coin when a passed level showed no fullscreen ad"
        description="Same rule as MiniPix, on Gyanni_quiz_ads_config. Counted per quiz level. A banner on the question does not count. Loads, no-fill, and failures do not count as shown. If the user passes and no fullscreen or watch ad was shown, they get this extra coin on top of the normal correct-answer coins. If one or more of those ads were shown, this extra coin is 0 and they keep the normal reward. Leaving unfinished pays 0. Until these knobs are saved, every passed zero-ad level pays 1 coin. 0 turns the payout off. Same passed level is paid at most once. Live on the next app config poll — no deploy."
      />
      <Space size="large" wrap>
        <Space direction="vertical">
          <span>Passed levels with zero ads (0 = off)</span>
          <InputNumber
            min={0}
            max={500}
            precision={0}
            value={gyanniZeroAdLevelThreshold}
            onChange={(v) => setGyanniZeroAdLevelThreshold(v ?? 0)}
          />
        </Space>
        <Space direction="vertical">
          <span>Max ads shown and still give the coin (0 = only when none shown)</span>
          <InputNumber
            min={0}
            max={500}
            precision={0}
            value={gyanniZeroAdMaxAdsShown}
            onChange={(v) => setGyanniZeroAdMaxAdsShown(v ?? 0)}
          />
        </Space>
        <Space direction="vertical">
          <span>Coins to give</span>
          <InputNumber
            min={0}
            max={100}
            precision={0}
            value={gyanniZeroAdCoinAmount}
            onChange={(v) => setGyanniZeroAdCoinAmount(v ?? 0)}
          />
        </Space>
      </Space>
      <div style={{ marginTop: 20 }}>
        <Button
          type="primary"
          loading={savingGyanniZeroAdCoin}
          onClick={() => {
            setSavingGyanniZeroAdCoin(true);
            Put(
              `${apiUrl}/v4/app_config/admin/gyanni-quiz-zero-ad-coin`,
              token,
              {
                zeroAdCoinLevelThreshold: Number(gyanniZeroAdLevelThreshold) || 0,
                zeroAdCoinMaxAdsShown: Number(gyanniZeroAdMaxAdsShown) || 0,
                zeroAdCoinAmount: Number(gyanniZeroAdCoinAmount) || 0,
              },
              (result) => {
                setSavingGyanniZeroAdCoin(false);
                if (result.success) {
                  notification.success({
                    message: "Success",
                    description: "Gyanni zero-ad coin updated — live on the next app config poll",
                  });
                } else {
                  notification.error({
                    message: "Error updating Gyanni zero-ad coin",
                    description: (result.detail && result.detail.message) || "Check console for more details",
                  });
                }
              },
              () => {
                setSavingGyanniZeroAdCoin(false);
                notification.error({
                  message: "Error updating Gyanni zero-ad coin",
                  description: "Check console for more details",
                });
              }
            );
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default AppConfig;
